import React, {useState, useEffect, useCallback, useMemo} from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../utils/supabaseClient";
import {PostgrestResponse, PostgrestSingleResponse} from '@supabase/supabase-js';
import styles from "../tournamentPage/tournamentPage.module.css";
import ClubCard from "../clubCard/clubCard";
import {useGetFutureMatches, useGetPastMatches} from '../../hooks/useGetMatches';
import Header from "../header/header";
import NewsBlock from "../newsBlock/newsBlock";
import MatchCardM from "../matchCardM/matchCardM";
import MatchCardS1x3 from "../matchCardS1x3/matchCardS1x3";

const tournamentMenu = [
    {
        name: 'Главная',
        link: ''
    },
    {
        name: 'О турнире',
        link: '/about'
    },
    {
        name: 'Участники',
        link: '/teams'
    },
    {
        name: 'Календарь',
        link: '/calendar'
    },
    {
        name: 'Матч-центр',
        link: '/matches'
    },
    {
        name: 'Таблица',
        link: '/table'
    },
    {
        name: 'Статистика',
        link: '/stats'
    },
    {
        name: 'История',
        link: '/stats'
    },
    {
        name: 'Новости',
        link: '/news'
    },
    {
        name: 'Медиа',
        link: '/media'
    }
]

interface Tournament {
    id: number;
    name: string;
    short_name: string;
    fk_parent: number;
    fk_season: {
        name: string;
    };
    team2tournament?: {
        fk_team: {
            team_short: TeamShort;
        }
    }[]
}

interface TournamentOtherSeasons {
    id: number;
    name: string;
    short_name: string;
    fk_parent: number;
    fk_season: {
        name: string;
    }[];
}

interface TeamShort {
    team_id: number;
    team_club_id: number;
    team_club_short_name: string;
    team_club_logo: string;
    team_club_city: string;
    team_club_country: string;
}

const PAST_MATCHES_COUNT = 4;
const FUTURE_MATCHES_COUNT = 9;

const TournamentPage: React.FC = () => {
    console.log("TournamentPage render");
    const { tournamentId } = useParams<{ tournamentId: string }>();
    const [tournament, setTournament] = useState<Tournament | null>(null);
    const [tournamentSeasons, setTournamentSeasons] = useState<TournamentOtherSeasons[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    console.log('tournament id:', tournamentId)

    const matchOptions = useMemo(() => ({
        tournamentId: tournamentId ? Number(tournamentId) : undefined
    }), [tournamentId]);

    const {
        matches: pastMatches,
        loading: pastMatchesLoading,
        error: pastMatchesError
    } = useGetPastMatches(PAST_MATCHES_COUNT, matchOptions);

    const {
        matches: futureMatches,
        loading: futureMatchesLoading,
        error: futureMatchesError
    } = useGetFutureMatches(FUTURE_MATCHES_COUNT, matchOptions);

    console.log('future matches', futureMatches)
    const futureMatches1 = futureMatches.slice(0,3);
    const futureMatches2 = futureMatches.slice(3,6);
    const futureMatches3 = futureMatches.slice(6,9);

    console.log('fut1', futureMatches1)
    console.log('fut2', futureMatches2)
    console.log('fut3', futureMatches3)


    const fetchTournamentData = useCallback(async () => {
        if (!tournamentId) {
            setError('Tournament ID is missing');
            setIsLoading(false);
            return;
        }

        try {
            const { data: tournamentData, error: tournamentError }: PostgrestSingleResponse<Tournament> = await supabase
                .from('tournaments')
                .select(`
                    id,
                    name,
                    short_name,
                    fk_parent,
                    fk_season!inner (
                        name
                    ),
                    team2tournament (
                        fk_team (
                            team_short:teams_short!inner (
                                *
                            )
                        )
                    )
                `)
                .eq('id', tournamentId)
                .single();

            if (tournamentError) throw tournamentError;
            if (!tournamentData) throw new Error('Tournament not found');

            setTournament(tournamentData);
            console.log('Данные турнира:', tournamentData);
        } catch (err) {
            console.error('Error fetching tournament data:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    }, [tournamentId]);

    const fetchTournamentOtherSeasons = useCallback(async () => {
        if (!tournament?.fk_parent) {
            setError('Tournament parent is missing');
            setIsLoading(false);
            return;
        }
        try {
            const { data: tournamentSeasonsData, error: tournamentSeasonsError } = await supabase
                .from('tournaments')
                .select(`
                id,
                name,
                short_name,
                fk_parent,
                fk_season!inner (
                    name
                )
            `)
                .eq('fk_parent', tournament.fk_parent);

            if (tournamentSeasonsError) throw tournamentSeasonsError;
            if (!tournamentSeasonsData || tournamentSeasonsData.length === 0) throw new Error('Tournament seasons not found');

            setTournamentSeasons(tournamentSeasonsData);
            console.log('Tournament seasons data:', tournamentSeasonsData);
        } catch (err) {
            console.error('Error fetching tournament seasons data:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    }, [tournament?.fk_parent]);

    useEffect(() => {
        fetchTournamentData();
    }, [fetchTournamentData]);

    useEffect(() => {
        if (tournament?.fk_parent) {
            fetchTournamentOtherSeasons();
        }
    }, [tournament, fetchTournamentOtherSeasons]);

    useEffect(() => {
        console.log("Past matches updated:", pastMatches.length);
    }, [pastMatches]);

    if (isLoading || pastMatchesLoading) {
        return <div>Loading tournament details...</div>;
    }

    if (error || pastMatchesError) {
        return <div>Error: {error || pastMatchesError?.message}</div>;
    }

    if (!tournament) {
        return <div>Tournament not found</div>;
    }

    return (
        <>
            <Header title={tournament.short_name} currentSeason={tournament.fk_season.name} seasons={tournamentSeasons} menu={tournamentMenu} />
            <h2 className={styles.section__header}>Tournament news</h2>
            <NewsBlock/>
            <div className="tournament-page">
                <h2 className={styles.section__header}>Календарь</h2>
                <div className={styles.matches_S_container}>
                    <MatchCardS1x3 matches={futureMatches1} />
                    <MatchCardS1x3 matches={futureMatches2} />
                    <MatchCardS1x3 matches={futureMatches3} />
                </div>
                <h2 className={styles.section__header}>Прошедшие матчи</h2>
                <ul className={styles.matches}>
                    {pastMatches.map((match) => (
                        <MatchCardM match={match} key={match.id} />
                    ))}
                </ul>
                <h2 className={styles.section__header}>Teams</h2>
                <ul className={styles.clubs_list}>
                    {tournament.team2tournament?.map((club) => (
                        <ClubCard key={club.fk_team.team_short.team_id} club={club.fk_team.team_short} />
                    ))}
                </ul>
            </div>
        </>
    );
};

export default React.memo(TournamentPage);
